import type { Locale } from './types'
import { enParadigms } from './paradigms/en'
import { enLabDemos } from './lab-demos/en'
import { enIndustryDemos } from './industries/en'
import { enMethod } from './method/en'
import { enCases } from './cases/en'
import { enPlaybook } from './playbook/en'

export const en: Locale = {
  meta: {
    title: 'Orion AI',
    description: 'Ontology — The Semantic Foundation for Enterprise AI.',
    pageTitle: 'Orion AI · Three-Stage Map of Enterprise AI',
  },
  langSwitch: {
    ariaLabel: 'Switch language',
  },
  nav: {
    home: 'Industries',
    paradigms: 'Paradigms',
    demo: 'Demo',
    lab: 'Lab',
    industries: 'Industries',
    method: 'Method',
    cases: 'Case Studies',
    playbook: 'Playbook',
    menuAriaLabel: 'Main menu',
  },
  industryDemos: enIndustryDemos,
  header: {
    enterDemo: 'Enter Demo →',
    backHome: '← Back to Home',
    logoLabel: 'Back to home',
  },
  home: {
    hero: {
      titleLine1: 'Ontology',
      titleLine2: 'The Semantic Foundation for Enterprise AI',
    },
    whatIs: {
      sectionTag: '01 · What it is',
      titleA: 'Ontology is the ',
      titleSpan: 'semantic operating system of the enterprise',
      oneLineLabel: 'In one line',
      oneLineBefore: 'Formalize the enterprise\'s ',
      oneLineObj: 'Objects',
      oneLineRel: 'Relations',
      oneLineCon: 'Constraints',
      oneLineAct: 'Actions',
      oneLineAfter: ' so people and AI share a single source of truth.',
      solvesLabel: 'Problems it solves',
      solvesItems: [
        'Data silos: same "customer" / "delivered" / "equivalent" means different things in different systems',
        'Semantic drift: change a threshold once, Skill / RAG / MCP fail to keep up',
        'Rigid SOPs: Skills hardcode one path; change the goal, rebuild from scratch',
        'No traceability: AI decisions cannot be traced back to objects, rules, data sources',
      ],
      ontologyAiLabel: 'Ontology + AI = Auto-orchestration',
      ontologyAiBody1:
        'When solving with ontology, AI agents do not follow predefined workflows — ',
      ontologyAiSpan: 'they read the ontology and synthesize the path live',
      ontologyAiBody2:
        '. Goal changes → path changes; data changes → path changes; constraint changes → path changes. Every node and edge is synthesized in the moment, not stored as an SOP.',
    },
    upgrades: {
      sectionTag: '02 · What it lifts in your enterprise AI transformation',
      titleA: 'Four leaps from ',
      titleSpan: '"usable"',
      titleB: ' to "trustworthy"',
      items: [
        {
          title: 'Determinism',
          body: 'Same question → same set of answers. Ontology pins "equivalent" / "delivered" / "customer" to one definition across every Skill / RAG / MCP.',
        },
        {
          title: 'Stability',
          body: 'AI no longer depends on ad-hoc prompts. Goal + data + constraints drive path synthesis; change one constraint and the whole graph propagates.',
        },
        {
          title: 'Observability',
          body: 'Any AI conclusion expands into an explainable chain of objects → relations → constraints → data sources. Incident review moves from "check logs" to "read the graph".',
        },
        {
          title: 'Governability',
          body: 'The ontology is the enterprise\'s "AI constitution". Modifying it goes through change control — like editing an ISO process. AI behavior becomes governable, not just debuggable.',
        },
      ],
    },
    industriesSection: {
      sectionTag: '03 · Ontology applications across 11 industries',
      titleA: 'Every industry has ',
      titleSpan: 'its own semantic foundation',
      leadA: 'The objects / relations / constraints / actions differ — but ',
      leadSpan: '"AI synthesizing paths on the foundation"',
      leadB: ' is universal.',
      labels: {
        applications: 'Typical applications',
        coreObjects: 'Core ontology objects',
        value: 'Ontology value',
        scenario: 'Real scenario',
      },
    },
    bottomCta: {
      sectionTag: 'Want to see a real workflow?',
      titleLine1: 'Open the Orion AI Three-Stage Demo',
      titleLine2: 'The gap between L1 / L2 / L3, in one screen',
      body: 'Based on a real onshore oil & gas operator equipment emergency-procurement scenario — three interactions (chat / call stack / workflow) reveal the core value of ontology.',
      button: 'Enter Demo',
    },
  },
  demoHero: {
    tag: 'Onshore Oil & Gas Equipment Procurement',
    titleLine1: 'Enterprise AI in',
    titleLine2: 'Three Acts',
    para1: {
      arrowBefore: '',
      midA: 'Generic LLM',
      midB: 'Enterprise AI Stack (RAG + MCP + Skill)',
      midC: 'AI + Ontology',
      arrowAfter: '.',
    },
    para2: {
      lead: 'The first two stages "add capabilities to the LLM"; the third is a ',
      spanA: 'paradigm leap',
      tail: ' — AI no longer follows workflows; it reads the ontology and synthesizes the path on the fly.',
    },
    storyCard: {
      alertTag: 'Alarm K-3041',
      alertMeta: '02:14 · CGS-3 · P-1023A',
      body:
        'Pre-dawn, Tarkim Gas Field central processing station. Stage-1 seal leak on the high-sour feed pump. A 2-week RFQ was pushed; three bids returned, each with deviations.',
      bodyHaze: 'Same question — what would Orion AI say in three different versions?',
    },
  },
  industries: [
    {
      id: 'finance',
      code: 'FIN',
      name: 'Finance & Banking',
      nameEn: 'Finance & Banking',
      applications: ['AML / KYC', 'Customer 360', 'Risk Attribution', 'Fraud Graph'],
      coreObjects: ['Customer', 'Account', 'Transaction', 'Risk Event', 'Regulatory Rule', 'Blacklist'],
      benefit:
        'Unify "customer" / "transaction" / "account" / "risk event" semantics into a single graph — risk, compliance, and relationship managers share the same source of truth. One policy change propagates globally; new compliance rules apply automatically to all in-flight transactions.',
      scenario:
        'Central bank raises rates 25 bp → ontology automatically recalculates PD (probability of default) for all mortgage customers, flags 12,400 high-risk accounts, triggers KYC re-review, notifies the corresponding RM, and syncs with regulator reporting.',
    },
    {
      id: 'insurance',
      code: 'INS',
      name: 'Insurance',
      nameEn: 'Insurance',
      applications: ['Claims Automation', 'Fraud Detection', 'Underwriting', 'Product / Clause'],
      coreObjects: ['Insured', 'Policy', 'Claim Event', 'Medical Record', 'Vehicle / Property', 'Anti-fraud Rules'],
      benefit:
        '"Insured" / "policy" / "event" / "medical record" / "claim rules" tied into one ontology. The same claim is reasoned in parallel across hospital diagnosis + police report + history + anti-fraud rules; the AI synthesizes the action.',
      scenario:
        'A motor-claim report → ontology jointly invokes hospital diagnosis + police scene report + historical claims + fraud model, and gives "approve / review / deny" with an explainable reasoning chain in 5 seconds.',
    },
    {
      id: 'automotive',
      code: 'MFG',
      name: 'Automotive & Manufacturing',
      nameEn: 'Automotive & Manufacturing',
      applications: ['BOM Master', 'Quality Traceability', 'Supply Chain Visibility', 'Digital Twin'],
      coreObjects: ['Part', 'Batch', 'Routing', 'Assembly', 'Vehicle', 'Defect Record'],
      benefit:
        'Parts, batches, routings, vehicles, defects, full traceability — all ontologized. When a quality issue surfaces, the ontology back-traces along assembly + material + process to the impact set; standard upgrades propagate globally.',
      scenario:
        'A part batch is found defective → ontology back-traces along the BOM to 86 already-sold vehicles + 12 WIP units on the line, auto-generates a recall list + remediation SOP + supplier-claim evidence pack.',
    },
    {
      id: 'retail',
      code: 'RTL',
      name: 'Retail & Consumer',
      nameEn: 'Retail & Consumer',
      applications: ['Product Master', 'Personalization', 'Dynamic Replenishment', 'Promo Optimization'],
      coreObjects: ['SKU', 'Category / Brand', 'Customer Profile', 'Channel', 'Inventory', 'Promo Rule'],
      benefit:
        'Product / SKU / brand / category / customer preference share one semantic. New launches, promos, price changes happen once — all channels (stores / e-com / social / 3P) sync automatically; no more cross-channel drift.',
      scenario:
        'A new product launches → ontology pushes it to 5 e-com channels + 32 retail stores + social-commerce communities + 3P distributors; price / copy / images / inventory all sync, no per-platform manual setup.',
    },
    {
      id: 'healthcare',
      code: 'HLT',
      name: 'Healthcare',
      nameEn: 'Healthcare',
      applications: ['Clinical Decision Support', 'Patient Profiles', 'Medical KG', 'Drug Interaction'],
      coreObjects: ['Patient', 'Symptom', 'Diagnosis', 'Drug', 'Test', 'Clinical Guideline'],
      benefit:
        'Patient, symptom, diagnosis, treatment, drug, interaction tied into one ontology. New clinical guidelines apply automatically to every matching patient\'s reasoning path; departments share the same medical semantics — no more siloed systems.',
      scenario:
        'A high-fever emergency patient → ontology synthesizes EHR history + current symptoms + test results, produces ordered differential diagnoses + required tests + drug contraindications + referral suggestion — giving the physician a 1-minute decision window.',
    },
    {
      id: 'legal',
      code: 'LGL',
      name: 'Legal & Compliance',
      nameEn: 'Legal & Compliance',
      applications: ['Contract Analysis', 'Legal Search', 'Compliance Review', 'Regulatory Tracking'],
      coreObjects: ['Statute', 'Case Law', 'Contract', 'Party', 'Compliance Item', 'Regulatory Doc'],
      benefit:
        'Statutes, case law, contract clauses, regulatory rules formalized as ontology. When new regulation lands, ontology auto-identifies which existing contracts / processes / business lines are affected; compliance moves from "manual line-by-line" to "graph-level coverage".',
      scenario:
        'A new data-protection law lands → ontology scans 1,200 active contracts + 86 internal processes, flags 87 contracts needing clause changes + 12 processes needing redesign, and auto-drafts amendment proposals.',
    },
    {
      id: 'logistics',
      code: 'LOG',
      name: 'Logistics & Supply Chain',
      nameEn: 'Logistics & Supply Chain',
      applications: ['End-to-end Tracking', 'Exception Attribution', 'Demand Forecast', 'Transport Optimization'],
      coreObjects: ['Order', 'SKU', 'Carrier', 'Customs / Docs', 'Waybill', 'Customer SLA'],
      benefit:
        'Order, carrier, customs, waybill, inventory, customer SLA in one graph. An "order exception" ticket — ontology traces end-to-end across the chain to root-cause + responsible party + remediation; no more cross-system manual reconciliation.',
      scenario:
        'Customer X opens a ticket "shipment not arrived" → ontology stitches data from SAP / WMS / TMS / Customs in 10 seconds, locates "stuck at Brazilian customs, missing ANVISA certificate", returns a remediation path + responsible party + ETA.',
    },
    {
      id: 'realestate',
      code: 'REA',
      name: 'Real Estate',
      nameEn: 'Real Estate',
      applications: ['Property Management', 'Market Valuation', 'Lease Optimization', 'Asset Compliance'],
      coreObjects: ['Property', 'Tenant', 'Valuation Factor', 'Market Data', 'Policy Constraint', 'Maintenance'],
      benefit:
        'Properties, listings, tenants, valuation drivers, market data, policy constraints all ontologized. Policy changes (purchase limits / interest rates / taxes) — ontology revalues every asset and flags affected contracts automatically.',
      scenario:
        'A city tightens its purchase-restriction policy → ontology revalues 3,400 assets, flags 124 affected listings + 47 leases needing renegotiation, triggers client notifications + finance reserve adjustments — full-company response in 2 hours.',
    },
    {
      id: 'energy',
      code: 'ENG',
      name: 'Energy',
      nameEn: 'Energy (Power / Oil & Gas / Renewables)',
      applications: ['Asset / Equipment Procurement', 'HSE Compliance', 'Dispatch / Trading', 'Carbon Tracking'],
      coreObjects: ['Equipment', 'Operating Condition', 'Standards (API / NACE / ISO)', 'Supplier AVL', 'Compliance Rule', 'MOC Record'],
      benefit:
        'Equipment, operating condition, standards (API / NACE / ISO), supplier AVL, compliance rules — unified in one ontology. This is the scenario in Orion\'s current demo. One standard upgrade propagates across the graph, identifying affected assets and in-flight procurements.',
      scenario:
        'An offshore platform\'s H₂S concentration suddenly spikes → ontology triggers material-compliance re-check + emergency AVL supplier search + MOC change flow + HSE report generation, producing three-tier remediation plans + risk grades in 24 hours.',
    },
    {
      id: 'education',
      code: 'EDU',
      name: 'Education & Training',
      nameEn: 'Education & Training',
      applications: ['Knowledge Graph', 'Personalized Path', 'Competency Assessment', 'Auto Content'],
      coreObjects: ['Knowledge Point', 'Skill', 'Course', 'Learner Profile', 'Learning Goal', 'Assessment Metric'],
      benefit:
        'Knowledge points, skills, courses, learner profiles, goals all formalized. When a learner\'s goal changes (career switch / level-up / new interest), AI re-synthesizes a personalized learning path on the ontology — not picking from a fixed course catalog.',
      scenario:
        'A learner transitions from PM to Data Analyst → ontology, based on current skills + target-role gap, recommends 18 courses + 4 hands-on projects + 3 must-read books, sequenced into a 12-week personalized path.',
    },
    {
      id: 'government',
      code: 'GOV',
      name: 'Government & Public Services',
      nameEn: 'Government & Public Services',
      applications: ['Cross-dept Data Share', 'Policy Impact Analysis', 'One-Stop Citizen Services', 'Emergency Response'],
      coreObjects: ['Citizen', 'Enterprise', 'Policy', 'Event', 'Geography', 'Departmental Responsibility'],
      benefit:
        'Citizens, enterprises, policies, events, geography, departmental responsibilities all tied to one ontology. When a new policy is enacted, the affected populations / departments / processes / service items become automatically visible — one-stop service moves from "system integration" to "shared semantics".',
      scenario:
        'A city issues "elderly subsidy +20%" → ontology automatically identifies 42,800 eligible citizens, notifies the community / district / finance / bank in parallel, schedules a disbursement plan + an objection-handling flow.',
    },
  ],
  layers: {
    L1: {
      shortLabel: 'L1 · LLM',
      techName: 'Generic LLM',
      oneLiner: 'Model weights are all the knowledge — talks well, but knows nothing of your world',
      newCapability: 'Talks',
      fatalGap: 'No enterprise data — every concrete decision sounds like an encyclopedia entry',
    },
    L2: {
      shortLabel: 'L2 · Enterprise AI Stack',
      techName: 'RAG + MCP + Skill',
      oneLiner: 'Documents, tools, and SOPs added to the LLM — it can read, act, and follow procedure',
      newCapability: 'Reads / Acts / Follows SOP',
      fatalGap:
        'Procedures are hardcoded · Skills don\'t share semantics · A threshold change means editing every Skill · Blind to anything outside the RFQ',
    },
    L3: {
      shortLabel: 'L3 · +Ontology',
      techName: 'AI + Ontology (Semantic Foundation)',
      oneLiner: 'AI no longer follows workflows — it reads the ontology and synthesizes the path live',
      newCapability: 'Thinks / Self-orchestrates',
      fatalGap: null,
    },
  },
  layerSection: {
    fatalGapLabel: 'Fatal gap',
    pivotLabel: 'Pivot',
    pivotBody1: 'Ontology is not another capability bolted on — it gives the previous two layers a shared ',
    pivotSemanticSpan: 'semantic foundation',
    pivotBody2: '. The AI agent then ',
    pivotPathSpan: 'synthesizes the path on the fly',
    pivotBody3: ' instead of executing a pre-written SOP. Only then can the enterprise reach the ',
    pivotFourQualitiesSpan: 'determinism / stability / observability / governability',
    pivotBody4: ' it actually asks for.',
    highlightBadge: 'Highlight',
  },
  l1: {
    header: 'Orion · L1 LLM only',
    noDataBadge: 'No enterprise data',
    rerunButton: '▶ Re-run',
    inputPlaceholder: 'Ask Orion …',
    protocolTag: 'chat completion',
    bubbleNoSource: 'No enterprise data · generic knowledge',
    chat: [
      {
        who: 'user',
        meta: 'M. Chen · 09:31',
        text: 'We\'re picking among three seals — A bids $182k Plan 53A, B bids $356k SS316 ex-stock, C bids $224k NACE cert 2024-08. Medium is high-sour natural gas. Which one?',
      },
      {
        who: 'ai',
        meta: 'Orion · L1',
        text: 'For high-sour-gas seal selection follow API 682. Generally consider material sulfide resistance (NACE MR0175); API plan selection (driven by vapor pressure); certificate version timeliness. A\'s Plan 53A is unpressurised — for low-vapor-pressure media. B\'s SS316 is austenitic — caution under H₂S and high chloride. C\'s certificate version should be verified. Please evaluate against your concrete operating parameters.',
      },
      {
        who: 'user',
        meta: 'M. Chen · 09:32',
        text: '… that\'s an encyclopedia. I asked which one.',
      },
    ],
  },
  l2: {
    rerunButton: '▶ Re-run',
    tabs: {
      rag: { label: 'RAG', caption: 'Let AI read enterprise docs' },
      mcp: { label: 'MCP', caption: 'Let AI call enterprise systems' },
      skill: { label: 'Skill', caption: 'Let AI follow SOPs' },
    },
    sharedLimitationLabel: 'Shared ceiling',
    sharedLimitation:
      'The three pieces don\'t share semantics. Skill SOPs are hardcoded; the world outside the RFQ stays invisible; threshold upgrades have to be applied Skill by Skill; the same question with a different goal forces a Skill rewrite.',
    rag: {
      chat: [
        {
          who: 'user',
          meta: 'M. Chen · 09:35',
          text: 'Compare these three against the P-1023 datasheet.',
        },
        {
          who: 'ai',
          meta: 'Orion · L2',
          text: 'Per [DS-OF-TQM-CGS3-U200-P1023.pdf §4.2]: this pump requires API 682 Plan 53B + Inconel 625 or equivalent, NACE MR0175 current version. Per [NACE/ISO 15156-3:2025-03 §A.2.1.3]: when H₂S partial pressure > 0.3 kPa and Cl⁻ > 10,000 mg/L, SS316 is forbidden. All three bids deviate from the datasheet — please review line by line.',
        },
      ],
      citationsLabel: 'Retrieved citations · 3',
      citations: [
        {
          id: 'c1',
          ref: 'DS-OF-TQM-CGS3-U200-P1023.pdf §4.2',
          quote: 'Pump requires API 682 Plan 53B + Inconel 625 or equivalent, NACE MR0175 current version …',
        },
        {
          id: 'c2',
          ref: 'NACE/ISO 15156-3:2025-03 §A.2.1.3',
          quote: 'When H₂S partial pressure > 0.3 kPa and Cl⁻ > 10,000 mg/L, SS316 / 316L is forbidden …',
        },
        {
          id: 'c3',
          ref: 'Group PA-2024-117 §8.4',
          quote: 'Emergency SLA 7-14 days; if value < $300k, approved at the oilfield subsidiary level …',
        },
      ],
      inputPlaceholder: 'Compare these three against P-1023 datasheet …',
      protocolTag: 'retrieve + read',
      hasCitationBadge: 'Cites enterprise doc',
    },
    mcp: {
      promptMeta: 'M. Chen 09:42',
      userPrompt: 'Cross-check A / B / C bids against the P-1023 datasheet and pull AVL status.',
      callStackLabel: 'Call stack',
      protocolNote: 'MCP / model context protocol',
      toolCalls: [
        { id: 't1', system: 'EAM', description: 'getAsset("P-1023") · pull operating baseline', resultLine: 'H₂S 12.6% / Cl⁻ 18,300 / 21 MPa / 95°C / VP 0.42' },
        { id: 't2', system: 'SAP', description: 'getRFQ("29117") · pull three bids', resultLine: 'A: Plan 53A · B: SS316 ex-stock · C: NACE 2024-08' },
        { id: 't3', system: 'AVL', description: 'queryStatus([A,B,C], scope=[group,field])', resultLine: 'A ✓✓ · B ✓✓ · C group ✓ field SUSPENDED' },
        { id: 't4', system: 'QMS', description: 'getCertExpiry("C", "NACE MR0175")', resultLine: 'v2024-08 < current v2025-03' },
        { id: 't5', system: 'PI', description: 'getOperatingTrend("P-1023", last_30d)', resultLine: 'Measured peak H₂S 14.1%, above RFQ nominal value' },
      ],
      matrixTitle: 'Compliance matrix · auto-generated',
      matrixBadge: 'No fully compliant bid',
      matrixHeaders: { item: 'Item', note: 'Note' },
      matrix: [
        { item: 'API plan', a: 'block', b: 'pass', c: 'pass', note: 'A: 53A unpressurised, VP 0.42 needs 53B+' },
        { item: 'Material', a: 'pass', b: 'block', c: 'pass', note: 'B: SS316 forbidden in sour service' },
        { item: 'NACE cert', a: 'pass', b: 'pass', c: 'block', note: 'C: 2024-08 superseded' },
        { item: 'AVL · Group', a: 'pass', b: 'pass', c: 'pass', note: '' },
        { item: 'AVL · Field', a: 'pass', b: 'pass', c: 'block', note: 'C: suspended 2024-11' },
        { item: 'Lead time', a: 'pass', b: 'block', c: 'pass', note: 'B: 21d > SLA 14d' },
      ],
    },
    skill: {
      skillPrefix: 'Skill · ',
      version: 'v2.4.0',
      progressBadge: '8 / 8 done',
      stepRowPrefix: '[{idx}/08]',
      steps: [
        { index: 1, title: 'Extract operating baseline' },
        { index: 2, title: 'Pull RFQ and three bids' },
        { index: 3, title: 'Match against datasheet (9 items)' },
        { index: 4, title: 'Three-tier AVL query' },
        { index: 5, title: 'Certificate validity check' },
        { index: 6, title: 'MOC trigger evaluation' },
        { index: 7, title: 'Historical-claim retrieval' },
        { index: 8, title: 'Generate compliance matrix + recommendation' },
      ],
      reportLabel: 'Standardised report · ready to sign and archive',
      recommendation:
        'No fully compliant bid this round. If the rule is "minimum MOC": recommend B + MOC-FAST (material upgrade); risk: 21-day lead vs. 9-12-day standby life. If the rule is "fastest lead time": recommend A + MOC-FAST (plan upgrade) + process-confirmation letter from the manufacturer.',
      pendingDecision: 'Please have the lead engineer / department director pick a decision rule and co-sign.',
      pendingTag: 'Awaiting human',
    },
  },
  l3: {
    selectGoalLabel: '① Pick a goal',
    selectGoalHint: 'Same ontology, different goal → completely different path',
    adjustConditionLabel: '② Adjust conditions',
    adjustConditionHint: 'Drag → the workflow re-synthesises live',
    constraintsBadgeTpl: '{count} hard constraints',
    slider: {
      h2sLabel: 'H₂S (%)',
      h2sHintHot: 'Sour service — NACE mandatory',
      h2sHintCold: 'Non-sour service',
      clLabel: 'Cl⁻ (mg/L)',
      clHintHot: 'High chloride — austenitic forbidden',
      clHintCold: 'Low chloride',
      vpLabel: 'VP-ratio',
      vpHintHot: 'High vapor pressure — API 53B+',
      vpHintCold: 'Low vapor pressure',
    },
    whiteboardTitle: 'Ontology workflow',
    nodesEdgesTpl: '· {n} nodes · {e} edges',
    attributionLabel: 'Reverse attribution',
    attributionClear: 'Clear ✕',
    attributionHint: 'Click a node → reverse attribution · dagre layout',
    bottomCaption: 'No node or edge is pre-written — all synthesised live by the ontology from goal + conditions.',
    pinButton: 'Approve →',
    goals: [
      { id: 'compliance', label: '① Compliance-first', caption: 'Pick a compliant seal for P-1023 in 2 weeks' },
      { id: 'uptime', label: '② No-stop', caption: 'How long can the standby run, how do we bridge' },
      { id: 'cost', label: '③ Min total cost', caption: 'Includes equipment + downtime + accident probability' },
    ],
    paths: {
      compliance: {
        goalLabel: 'G1 · Compliance-first',
        goalDetail: '"Pick a compliant seal for P-1023, 2-week ETA"',
        objLabel: 'Equipment::P-1023',
        objDetail: 'deref → hasOperatingCondition + hasDesignSpec',
        infCondLabel: 'Operating-condition inference',
        infCondDetailTpl: 'H₂S {h2s} · Cl⁻ {cl} · VP-ratio {vp}',
        rfqLabel: 'RFQ::29117',
        rfqDetail: 'Three bids A / B / C archived; parse spec / price / lead',
        cMatLabel: 'R-MAT-NACE-21',
        cMatDetail: 'Sour service + high chloride → forbid SS316',
        cMatCitation: 'NACE / ISO 15156-3',
        cPlanLabel: 'R-PLAN-VP-08',
        cPlanDetailTpl: 'VP-ratio {vp} > 0.3 → ban Plan 53A',
        cPlanCitation: 'API 682 §6.1.2.5',
        cNoneLabel: 'Mild conditions',
        cNoneDetail: 'No hard constraint fired — candidates move straight to price / lead comparison',
        candLabelViol: 'Candidate evaluation (with violations)',
        candLabelOk: 'Candidate evaluation (all compliant)',
        candDetailOk: 'Price / lead: A $182k / 7d · B $356k / 21d · C $224k / 14d',
        candViolPieceMat: 'B violates material',
        candViolPiecePlan: 'A violates plan',
        candViolPieceCert: 'C violates certificate + field AVL',
        infCovLabel: 'R-COV-09',
        infCovDetail: 'Proactive cross-RFQ coverage — found D, historical lead-time mean 15.8 ± 1.4 d',
        actLabelViol: 'expandRfq + raiseMOC',
        actLabelOk: 'awardBest',
        actDetailViol: 'Expand RFQ to invite D · start MOC-Fast-Track · standby P-1023B intensified inspection',
        actDetailOk: 'Based on price / lead / historical delivery → award A · file',
      },
      uptime: {
        goalLabel: 'G2 · Guaranteed uptime',
        goalDetail: '"How long can the standby run, how do we bridge"',
        objLabel: 'P-1023B (standby)',
        objDetail: 'standbyOf P-1023A · historical wear / last overhaul 2024-09',
        piLabel: 'PI live data',
        piDetail: 'Seal-face temp 92°C · vibration 3.2 mm/s · trend rising',
        infLifeLabel: 'Remaining-life estimate',
        infLifeDetailTpl: 'H₂S {h2s} corrosion factor {factor} → {lo}-{hi} days',
        cSlaLabel: 'R-BACKUP-SLA-11',
        cSlaDetail: 'Standby life < fastest compliant ETA → mandatory intensified inspection + load limit',
        cSlaCitation: 'Group EQ-BACKUP-SLA',
        candLabel: 'Bridge plan set',
        candDetail: 'Inspect every 4h · D custom 16d · partial load 80% · 16h scheduled outage',
        actLabel: 'schedInsp + degrade',
        actDetail: 'Intensified inspection + 80% load + expand RFQ to invite D · MOC not required',
      },
      cost: {
        goalLabel: 'G3 · Min total cost',
        goalDetail: '"Includes equipment + downtime + accident probability"',
        costModelLabel: 'CostModel',
        costModelDetail: 'Expected total cost = price + downtime loss × p + HSE penalty × p',
        riskModelLabel: 'RiskModel',
        riskModelDetail: 'Accident-probability function p(conditions, material, plan, historical claims)',
        infMcLabel: 'Monte Carlo (10k runs)',
        infMcDetailTpl: 'Conditions (H₂S {h2s} / Cl⁻ {clk}k / VP {vp}) → A: {a} · B: {b} · D: {d} ($10k)',
        cMocLabel: 'R-MOC-MANDATORY-01',
        cMocDetailNeeded: 'D involves spec deviation → MOC required, counts as 12k process cost',
        cMocDetailNotNeeded: 'No candidate deviates → MOC not required',
        cMocCitation: 'Group HSE-MOC-2024',
        candLabel: 'Ranking',
        candDetailTpl: 'Best = {best} ({cost}) · includes accident prob + MOC cost',
        actLabelAggressive: 'expandRfq + MOC + alert',
        actLabelMild: 'awardBest + report',
        actDetailAggressiveTpl: 'Expand RFQ to invite D · budget {d} ± 6 · sync to CFO',
        actDetailMildTpl: 'Award A · budget {a} · file',
      },
    },
    advantagesTitle: 'Eight enterprise-grade advantages of Ontology',
    advantages: [
      { title: 'Dynamic orchestration', body: 'Paths aren\'t hardcoded — same ontology, different goal → completely different execution chains, synthesised live.' },
      { title: 'Cross-domain consistency', body: '"Material equivalence" means one thing across procurement / engineering / maintenance — no more talking past each other.' },
      { title: 'Explainable', body: 'Every node expands → see the underlying objects, relations, constraints, and data sources.' },
      { title: 'Governable', body: 'Change one rule (e.g. the H₂S threshold), all Skills / RAGs / MCPs apply it automatically — no per-Skill editing.' },
      { title: 'Active cognition', body: 'Constraints run on the event stream — alerts and alternatives surface before the incident.' },
      { title: 'Self-correcting', body: 'Inferences can be overridden by higher-priority rules; governance warnings appear when you weaken a hard constraint.' },
      { title: 'Composable', body: 'The same ontology supports unbounded new goals — no retraining, no Skill rewrites.' },
      { title: 'Knowledge transfer', body: 'Plug in a new scenario and it inherits historical know-how; one field\'s experience flows to the next.' },
    ],
    kindLabels: {
      goal: 'Goal',
      object: 'Object',
      inference: 'Inference',
      constraint: 'Constraint',
      candidate: 'Candidate',
      action: 'Action',
    },
  },
  paradigms: enParadigms,
  labDemos: enLabDemos,
  method: enMethod,
  cases: enCases,
  playbook: enPlaybook,
  lab: {
    sectionTag: 'Lab',
    heroTitle: 'Interactive paradigm lab',
    heroSubtitle:
      'Each paradigm has its own playground. Trigger the user-side action on the left, watch the ontology operate on the right. Every demo is wired to the same semantic foundation — different shape, same plumbing.',
    sidebarTitle: 'Paradigms',
    tryItLabel: 'Try it →',
    comingSoonLabel: 'Coming soon',
    comingSoonBody:
      'This paradigm has a scenario lined up and will land in a later phase. For now, see the paradigm summary on the Paradigms page.',
    userActionLabel: 'User action',
    ontologyResponseLabel: 'Ontology response',
    backToParadigmsLabel: '← Back to all paradigms',
  },
  footer: {
    line: 'Orion AI · Three-Stage Map of Enterprise AI · Phase 2 Preview',
    versionPrefix: 'v0.2.0 · ',
  },
}
